import React, { createContext, useContext, useEffect, useState } from "react";

const ConfigContext = createContext();

export const useConfig = () => {
  const context = useContext(ConfigContext);
  if (!context) {
    throw new Error("useConfig must be used within ConfigProvider");
  }
  return context;
};

export const ConfigProvider = ({ children }) => {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadConfig = async () => {
      try {
        const response = await fetch("/config/site-config.json");
        const siteConfig = await response.json();

        // Apply CSS custom properties for theming
        const root = document.documentElement;
        root.style.setProperty(
          "--color-primary",
          siteConfig.theme.colors.primary
        );
        root.style.setProperty(
          "--color-secondary",
          siteConfig.theme.colors.secondary
        );
        root.style.setProperty(
          "--color-accent",
          siteConfig.theme.colors.accent
        );
        root.style.setProperty(
          "--gradient-start",
          siteConfig.theme.colors.gradientStart
        );
        root.style.setProperty(
          "--gradient-end",
          siteConfig.theme.colors.gradientEnd
        );

        // Set document title and meta tags
        document.title = siteConfig.seo.siteTitle.replace(
          "{companyName}",
          siteConfig.branding.companyName
        );

        // Update meta description
        let metaDesc = document.querySelector('meta[name="description"]');
        if (metaDesc) {
          metaDesc.setAttribute("content", siteConfig.seo.siteDescription);
        } else {
          metaDesc = document.createElement("meta");
          metaDesc.name = "description";
          metaDesc.content = siteConfig.seo.siteDescription;
          document.head.appendChild(metaDesc);
        }

        // Update favicon
        let favicon = document.querySelector('link[rel="icon"]');
        if (favicon) {
          favicon.href = siteConfig.branding.favicon;
        } else {
          favicon = document.createElement("link");
          favicon.rel = "icon";
          favicon.href = siteConfig.branding.favicon;
          document.head.appendChild(favicon);
        }

        // Load Google Fonts
        const fontLink = document.createElement("link");
        fontLink.href = `https://fonts.googleapis.com/css2?family=${siteConfig.theme.fonts.heading.replace(
          " ",
          "+"
        )}:wght@400;500;600;700&family=${siteConfig.theme.fonts.body.replace(
          " ",
          "+"
        )}:wght@300;400;500;600;700;800&display=swap`;
        fontLink.rel = "stylesheet";
        document.head.appendChild(fontLink);

        // Apply font families
        root.style.setProperty(
          "--font-heading",
          siteConfig.theme.fonts.heading
        );
        root.style.setProperty("--font-body", siteConfig.theme.fonts.body);

        setConfig(siteConfig);
      } catch (error) {
        console.error("Failed to load site configuration:", error);
        // Fallback to default config with all required fields
        setConfig({
          branding: {
            companyName: "BellaFlex",
            description: "Premium Bedding & Sleep Solutions",
            contactPhone: "",
            contactEmail: "",
            favicon: "/favicon.ico",
          },
          theme: {
            colors: {
              primary: "#203E90",
              secondary: "#E31419",
              accent: "#868686",
              gradientStart: "#203E90",
              gradientEnd: "#E31419",
            },
            fonts: {
              heading: "Playfair Display",
              body: "Inter",
            },
          },
          seo: {
            siteTitle: "BellaFlex - Premium Bedding & Sleep Solutions",
            siteDescription:
              "Shop premium mattresses, pillows, and bedding accessories.",
            keywords: "mattresses, pillows, bedding, sleep",
            ogImage: "/og-image.png",
          },
          features: {
            adminPanel: false,
            advancedFilters: true,
          },
        });
      } finally {
        setLoading(false);
      }
    };

    loadConfig();
  }, []);

  // Helper function to replace template variables
  const processContent = (text, variables = {}) => {
    if (!text) return "";

    let processed = text;
    if (config) {
      processed = processed.replace(
        /{companyName}/g,
        config.branding.companyName
      );

      Object.entries(variables).forEach(([key, value]) => {
        processed = processed.replace(new RegExp(`{${key}}`, "g"), value);
      });
    }

    return processed;
  };

  const value = {
    config,
    loading,
    processContent,
    // Convenience getters
    isFeatureEnabled: (feature) => config?.features?.[feature] || false,
    getBrandColor: (type) => config?.theme?.colors?.[type] || "#203E90",
    getContent: (key, variables) =>
      processContent(config?.content?.[key], variables),
  };

  return (
    <ConfigContext.Provider value={value}>{children}</ConfigContext.Provider>
  );
};
